import { motion } from "framer-motion";
import { DeliveryZone } from "./StoreSettingsContext";
import { LocationEditIcon, MapPin, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";

interface Props {
  deliveryZones: DeliveryZone[];
  t: (key: string, options?: any) => string;
  onZoneClick?: (zone: DeliveryZone) => void;
  onEditZone?: (zone: DeliveryZone) => void;
}

export default function DeliveryZonesList({ deliveryZones, t, onZoneClick, onEditZone }: Props) {
  if (!deliveryZones || deliveryZones.length === 0) {
    return (
      <div className="text-center py-8 px-4 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl border border-dashed border-gray-300">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full mb-4">
          <MapPin className="w-6 h-6 text-gray-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {t("DeliveryZonesList.empty.title")}
        </h3>
        <p className="text-gray-500 max-w-md mx-auto">
          {t("DeliveryZonesList.empty.description")}
        </p>
        <button className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-sm">
          {t("DeliveryZonesList.empty.addFirstZone")}
        </button>
      </div>
    );
  }

  // Group zones by status for better organization
  const activeZones = deliveryZones.filter(zone => zone.isActive);
  const inactiveZones = deliveryZones.filter(zone => !zone.isActive);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-xl mr-3 shadow-sm">
            <LocationEditIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-900">
              {t("DeliveryZonesList.title")}
            </h4>
            <p className="text-sm text-gray-500">
              {t("DeliveryZonesList.subtitle", { count: deliveryZones.length })}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full flex items-center">
            <CheckCircle className="w-4 h-4 mr-1" />
            {activeZones.length} {t("DeliveryZonesList.status.active")}
          </span>
          {inactiveZones.length > 0 && (
            <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full flex items-center">
              <XCircle className="w-4 h-4 mr-1" />
              {inactiveZones.length} {t("DeliveryZonesList.status.inactive")}
            </span>
          )}
        </div>
      </div>

      {/* Active Zones Section */}
      {activeZones.length > 0 && (
        <div>
          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            {t("DeliveryZonesList.sections.active")}
            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
              {activeZones.length}
            </span>
          </h5>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeZones.map((zone, i) => (
              <DeliveryZoneCard
                key={zone.id}
                zone={zone}
                index={i}
                t={t}
                isActive={true}
                onClick={onZoneClick}
                onEdit={onEditZone}
              />
            ))}
          </div>
        </div>
      )}

      {/* Inactive Zones Section */}
      {inactiveZones.length > 0 && (
        <div>
          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
            {t("DeliveryZonesList.sections.inactive")}
            <span className="ml-2 text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
              {inactiveZones.length}
            </span>
          </h5>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {inactiveZones.map((zone, i) => (
              <DeliveryZoneCard
                key={zone.id}
                zone={zone}
                index={i}
                t={t}
                isActive={false}
                onClick={onZoneClick}
                onEdit={onEditZone}
              />
            ))}
          </div>
        </div>
      )}

      {/* Summary Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl border border-gray-200"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-blue-500 mr-2" />
            <p className="text-sm text-gray-600">
              {t("DeliveryZonesList.summary.totalZones", { count: deliveryZones.length })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-700">
              {t("DeliveryZonesList.summary.averageFee")}
            </p>
            <p className="text-lg font-bold text-gray-900">
              {(
                deliveryZones.reduce((sum, zone) => sum + parseFloat(`${zone.fee}` || "0"), 0) / 
                deliveryZones.length
              ).toFixed(2)}{" "}
              {t("DeliveryZonesList.currency.symbol")}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Individual Zone Card Component
function DeliveryZoneCard({ 
  zone, 
  index, 
  t, 
  isActive, 
  onClick, 
  onEdit 
}: { 
  zone: DeliveryZone; 
  index: number; 
  t: (key: string, options?: any) => string; 
  isActive: boolean;
  onClick?: (zone: DeliveryZone) => void;
  onEdit?: (zone: DeliveryZone) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      className={`
        relative p-4 rounded-xl border transition-all duration-200 cursor-pointer
        ${isActive 
          ? "bg-gradient-to-r from-green-50 to-emerald-50/50 border-green-200 hover:border-green-300" 
          : "bg-gradient-to-r from-gray-50 to-gray-100/50 border-gray-200 hover:border-gray-300"
        }
      `}
      onClick={() => onClick?.(zone)}
    >
      {/* Status Badge */}
      <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${
        isActive 
          ? "bg-green-100 text-green-800" 
          : "bg-gray-100 text-gray-800"
      }`}>
        {isActive 
          ? t("DeliveryZonesList.status.active") 
          : t("DeliveryZonesList.status.inactive")
        }
      </div>

      {/* Edit Button */}
      {onEdit && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(zone);
          }}
          className="absolute top-3 left-3 p-1.5 text-gray-400 hover:text-blue-500 transition-colors rounded-lg hover:bg-white"
          aria-label={t("DeliveryZonesList.actions.edit")}
        >
          <LocationEditIcon className="w-4 h-4" />
        </button>
      )}

      <div className="pt-1">
        {/* Zone Name */}
        <div className="flex items-center mb-2">
          <div className={`p-2 rounded-lg mr-3 ${
            isActive 
              ? "bg-gradient-to-r from-green-100 to-emerald-100" 
              : "bg-gradient-to-r from-gray-100 to-gray-200"
          }`}>
            <MapPin className={`w-4 h-4 ${isActive ? "text-green-600" : "text-gray-500"}`} />
          </div>
          <h5 className="font-bold text-gray-900 text-lg truncate">
            {zone.city}
          </h5>
        </div>

        {/* Zone Details */}
        <div className="space-y-2">
          {/* Delivery Time */}
          <div className="flex items-center text-sm">
            <Clock className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-gray-600">
              {t("DeliveryZonesList.deliveryTime", { 
                days: zone.estimatedDays,
                dayText: zone.estimatedDays === 1 
                  ? t("DeliveryZonesList.time.day") 
                  : t("DeliveryZonesList.time.days")
              })}
            </span>
          </div>

          {/* Additional Info (if any) */}


          {/* Delivery Fee */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              {t("DeliveryZonesList.deliveryFee")}
            </span>
            <span className={`text-lg font-bold ${
              isActive ? "text-green-600" : "text-gray-600"
            }`}>
              {zone.fee} {t("DeliveryZonesList.currency.symbol")}
            </span>
          </div>

         
        </div>
      </div>
    </motion.div>
  );
}